import { GARAN_LABEL_IMAGE_PATH } from '../garan.config';
import { garanFont } from '../fonts';
import type { GaranDefaultProps } from '../garan.types';
import { Headline } from '../atoms/Headline/Headline';
import { Description } from '../atoms/Description/Description';

const DEFAULT_WARRANTY_SIZE = 66; // Default font size for warranty text in cqw units

/**
 * Props for the Garan label variant.
 */
export type GaranLabelProps = GaranDefaultProps;

/**
 * Displays the main Garan warranty label with warranty, brand, model, and optional localized description.
 *
 * The component scales dynamically based on the container size, maintaining the aspect ratio of the label image.
 *
 * @param warranty - The warranty text to display (e.g., "2,5").
 * @param brand - The brand name to display.
 * @param model - The model name to display.
 * @param localizedDescription - Optional localized description for accessibility.
 * @returns A JSX element representing the Garan label.
 */
export const GaranLabel = ({ warranty, brand, model, localizedDescription }: GaranLabelProps) => (
  <div
    className={`garan-label ${garanFont.className} @container relative w-full leading-none text-nowrap`}
    style={{
      backgroundImage: `url(${GARAN_LABEL_IMAGE_PATH})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      aspectRatio: '1 / 1.05',
    }}
  >
    <Headline />
    <div
      className="bg-color-background-base absolute flex flex-row justify-between tracking-tight"
      style={{ top: '24.6cqw', left: '2.1cqw', width: '95.4cqw', fontSize: '3.4cqw' }}
    >
      <div>{brand}</div>
      <div>{model}</div>
    </div>
    {localizedDescription && <Description localizedDescription={localizedDescription} />}
    <div
      className="bg-color-background-base @container absolute font-bold tracking-[-0.3em]"
      style={{ bottom: '49cqw', left: '3.1cqw', width: '40cqw', height: '22cqw' }}
    >
      <span
        className="absolute right-0"
        style={{
          fontSize: `${warranty.length < 4 ? DEFAULT_WARRANTY_SIZE : (DEFAULT_WARRANTY_SIZE * 3) / warranty.length}cqw`,
          bottom: '-0.15em',
        }}
      >
        {warranty}
      </span>
    </div>
  </div>
);
